import { formatCurrency, getCurrencyPrecision } from '@cast/utils';

import {
  getDiscoveredClusterSnapshotMetrics,
  getMonthlySavingsFromOptimization,
  useClusterDashboardState,
  useClusterOptimizationsQuery,
} from '@cast/features-cluster-shared';
import {
  useAssetListClustersQuery,
  useCluster,
} from '@cast/shared-cluster-context-data-access';

import { useAvailableSavingsQuery } from '@oneclick/hooks/queries/available-savings';

import { useChartsData } from '@cast/split-me/features/cluster/dashboard/GaugeChartsSection';

const parsePrice = (value?: string): number | undefined => {
  if (value == null || value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseSavingAmount = (
  priceBefore?: string,
  priceAfter?: string
): number | undefined => {
  const before = parsePrice(priceBefore);
  const after = parsePrice(priceAfter);

  if (before == null || after == null) {
    return undefined;
  }

  return Math.max(before - after, 0);
};

const getAvailableSavingsMonthly = (
  laymanMonthly?: { priceBefore?: string; priceAfter?: string },
  spotMonthly?: { priceBefore?: string; priceAfter?: string }
): number | undefined => {
  const laymanSavings = parseSavingAmount(
    laymanMonthly?.priceBefore,
    laymanMonthly?.priceAfter
  );
  const spotSavings = parseSavingAmount(
    spotMonthly?.priceBefore,
    spotMonthly?.priceAfter
  );

  return laymanSavings ?? spotSavings;
};

type PerformanceBannerMetrics = {
  cpuUtilizationPct?: number;
  monthlySavings?: number;
  savingsLabel: string;
  hasMetrics: boolean;
  isMetricsLoading: boolean;
};

export const usePerformanceBannerMetrics = (): PerformanceBannerMetrics => {
  const { cluster } = useCluster();
  const { isDiscovered } = useClusterDashboardState();
  const { cpu } = useChartsData();

  const { assetListClusters, isLoading: isAssetListLoading } =
    useAssetListClustersQuery({
      enabled: isDiscovered,
    });

  const assetCluster = assetListClusters?.find(
    ({ name }) => name === cluster.name
  );
  const snapshotMetrics = assetCluster
    ? getDiscoveredClusterSnapshotMetrics(assetCluster)
    : undefined;

  const { data: availableSavings, isLoading: isAvailableSavingsLoading } =
    useAvailableSavingsQuery(cluster.id, !isDiscovered);

  const availableSavingsMonthly = getAvailableSavingsMonthly(
    availableSavings?.recommendations?.Layman?.monthly,
    availableSavings?.recommendations?.SpotInstances?.monthly
  );

  const { data: optimizations, isLoading: isOptimizationsLoading } =
    useClusterOptimizationsQuery({
      clusterIds: [cluster.id],
      enabled: !snapshotMetrics?.monthlySavings && !availableSavingsMonthly,
    });

  const optimizationSavings = getMonthlySavingsFromOptimization(
    optimizations?.find((item) => item.clusterId === cluster.id)
  );

  const monthlySavings =
    snapshotMetrics?.monthlySavings ||
    availableSavingsMonthly ||
    optimizationSavings ||
    undefined;

  const savingsLabel =
    monthlySavings != null && monthlySavings > 0
      ? formatCurrency(monthlySavings, {
          maximumFractionDigits: getCurrencyPrecision(monthlySavings),
        })
      : 'significant savings';

  const gaugeHasMetrics = (cpu?.provisioned ?? 0) > 0;

  const cpuUtilizationPct = isDiscovered
    ? snapshotMetrics?.avgUtilizationPct
    : gaugeHasMetrics
    ? cpu?.usedPercentage
    : undefined;

  const hasMetrics =
    cpuUtilizationPct != null && !(cpuUtilizationPct === 0 && !monthlySavings);

  const isMetricsLoading =
    (isDiscovered && isAssetListLoading) ||
    (!isDiscovered &&
      (isAvailableSavingsLoading ||
        (isOptimizationsLoading && !gaugeHasMetrics)));

  return {
    cpuUtilizationPct,
    monthlySavings,
    savingsLabel,
    hasMetrics,
    isMetricsLoading,
  };
};
